import { useState, useEffect } from "react";
import classes from "../../styles/Modal.module.css";
import { getClasses, formatCurrency, replaceInTemplate } from "../../utils";
import ModalButton from "../buttons/ModalButton";
import { closeModal } from "../../store/slices/modals";
import { useDispatch } from "react-redux";
import { useTexts, useAppSelector } from "../../hooks";
import Modal, { ModalProps } from "./Modal";
import { clearMessage } from "../../store/slices/messages";

export type MessageType = {
  title?: string;
  message: string;
  timer?: number;
  amount?: string;
  placeholderValues?: string[];
  type?: "info" | "error";
  onClose?: () => void;
};

const Message: React.FC<ModalProps & MessageType> = ({
  title,
  message,
  shown,
  amount,
  type = "info",
  placeholderValues,
  timer,
  onClose,
  onHideEnd,
}) => {
  const { common } = useTexts();
  const dispatch = useDispatch();
  const currency = useAppSelector((state) => state.details.currency);
  const [timerValue, setTimerValue] = useState(timer);

  const closeHandler = () => {
    if (typeof timerValue === "number" && timerValue > 0) return;
    if (onClose) onClose();
    dispatch(closeModal());
  };

  const hideHandler = () => {
    if (onHideEnd) onHideEnd();
    dispatch(clearMessage());
  };

  const typeClass = type === "error" ? "error" : "";
  let messageText;

  useEffect(() => {
    if (timer) setTimerValue(timer);
  }, [timer]);

  useEffect(() => {
    if (typeof timerValue === "undefined" || timerValue < 0) {
      return;
    }

    const id = setInterval(() => {
      setTimerValue(timerValue - 1);
    }, 1000);

    return () => clearInterval(id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerValue]);

  const unclosable = typeof timerValue === "number" && timerValue > 0;
  const ok =
    timerValue && timerValue > -1 ? `${common.ok} (${timerValue})` : common.ok;

  // TODO this is a custom case for the "You have won...", which is not a good practice,
  // try to change it
  if (amount) {
    const sum = formatCurrency(amount, currency);
    messageText = `${common.congratulations} ${common.youverecieved} ${sum}`;
  } else {
    const values = placeholderValues || [];
    const template = common[message] || message;
    messageText = replaceInTemplate(template, ...(values as string[]));
  }

  const titleText = common[title ?? ""] || title;

  return (
    <Modal
      className={getClasses(classes, "message", typeClass)}
      shown={shown}
      icon="Info"
      name="message"
      title={titleText}
      unclosable={unclosable}
      onClose={closeHandler}
      onHideEnd={hideHandler}
    >
      <div className={classes["content-main"]}>
        <div className={classes.message}>{messageText}</div>
      </div>
      <div className={classes["content-button"]}>
        <ModalButton
          type={type === "error" ? "danger" : "accent"}
          soundEffect="button-click"
          className={classes.button}
          available={true}
          disabled={unclosable}
          onClick={closeHandler}
        >
          {ok}
        </ModalButton>
      </div>
    </Modal>
  );
};

export default Message;
